import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getChildCharacters } from '../../api/characters';
import { getTodaysChallenges } from '../../api/challenges';
import s from './NodeContextDrawer.module.css';

const DESKTOP_BREAKPOINT = 1280;
const SPRING = { type: 'spring', stiffness: 380, damping: 36 };
const MAX_LEARNING = 6;

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
  );
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

function CharCard({ row, t }) {
  const ch = row.characters;
  const isToLearn = row.state === 'to_learn';
  return (
    <div className={s.charCard}>
      <span className={s.charGlyph}>{ch?.simplified_char || '?'}</span>
      <span className={`${s.charState} ${isToLearn ? s.stateToLearn : s.statePracticing}`}>
        {isToLearn ? t('map.charStateNew') : t('map.charStatePractice')}
      </span>
    </div>
  );
}

/**
 * Drawer that opens when a map node is tapped.
 *
 * variant='current'   — child's active node. Shows learning characters,
 *                       a Practice CTA, and a one-line challenge summary
 *                       that, when tapped, swaps over to ChallengeDrawer.
 * variant='completed' — past node. Shows a small "you completed this level"
 *                       confirmation. No CTAs.
 */
export default function NodeContextDrawer({ open, variant, node, childId, onClose, onOpenChallenges }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const { data: chars } = useQuery({
    queryKey: ['characters', childId, 'learning'],
    queryFn: () => getChildCharacters(childId),
    enabled: !!childId && open && variant === 'current',
    staleTime: 60_000,
  });

  const { data: challenges } = useQuery({
    queryKey: ['challenges', childId],
    queryFn: () => getTodaysChallenges(childId),
    enabled: !!childId && open && variant === 'current',
    staleTime: 30_000,
  });

  const learning = (chars || [])
    .filter((c) => c.state === 'to_learn' || c.state === 'practicing')
    .slice(0, MAX_LEARNING);

  const drawerMotionProps = isDesktop
    ? {
        initial: { opacity: 0, scale: 0.92 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.92 },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        drag: 'y',
        dragConstraints: { top: 0, bottom: 0 },
        dragElastic: { top: 0, bottom: 0.4 },
        onDragEnd: (_, info) => {
          if (info.offset.y > 100 || info.velocity.y > 500) onClose();
        },
      };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={s.scrim}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={`${s.drawer} ${isDesktop ? s.drawerDesktop : ''}`}
            role="dialog"
            aria-label={variant === 'current' ? 'Current level' : 'Completed level'}
            transition={SPRING}
            {...drawerMotionProps}
          >
            <button
              type="button"
              className={s.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>

            <h2 className={s.title}>Level {node?.level}</h2>

            {variant === 'current' && (
              <>
                <section className={s.section}>
                  <div className={s.charsHeader}>
                    <p className={s.sectionTitle}>{t('map.yourCharacters')}</p>
                    {learning.length > 0 && (
                      <span className={s.charsHeaderProgress}>
                        {learning.length}/{MAX_LEARNING}
                      </span>
                    )}
                  </div>
                  {learning.length === 0 ? (
                    <p className={s.emptyChars}>{t('map.emptyCharacters')}</p>
                  ) : (
                    <div className={s.charGrid}>
                      {learning.map((row) => (
                        <CharCard key={row.id} row={row} t={t} />
                      ))}
                    </div>
                  )}
                </section>

                <button
                  type="button"
                  className={s.practiceBtn}
                  onClick={() => {
                    onClose();
                    navigate('/workbook');
                  }}
                >
                  <span className={s.practiceIcon} aria-hidden="true">🖌️</span>
                  <p className={s.practiceTitle}>{t('map.practiceWriting')}</p>
                </button>

                {challenges?.core && (
                  <div className={s.summaryWrapper}>
                    <span className={s.bonusPill}>{t('map.bonus')}</span>
                    <button
                      type="button"
                      className={s.summaryRow}
                      onClick={onOpenChallenges}
                    >
                      <span className={s.summaryLabel}>{challenges.core.display_name}</span>
                      <span className={s.summaryProgress}>
                        {challenges.core.completed_at
                          ? t('map.done')
                          : `${challenges.core.progress_count}/${challenges.core.target_count}`}
                      </span>
                    </button>
                  </div>
                )}
              </>
            )}

            {variant === 'completed' && (
              <div className={s.completedWrap}>
                <span className={s.completedBadge}>
                  <span aria-hidden="true">✓</span>
                  <span>{t('map.completed')}</span>
                </span>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
